import Layout from '@src/components/Layout';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FarmersQuery } from '~/QueryHooks/Farmers';
import i18n from '~/components/i18n';
import { RouteTypes } from '~/routes/constants';
import TableComponent from '~/components/TableComponent';
import {
  LIGHT_USER_ROLE_SLUG,
  FARMER_MANAGER_ROLE_SLUG,
  getFarmerTableColumnHeaders,
  MILK_MONEY_ACCOUNT_SLUG,
  MILK_FIXED_SLUG,
  MILK_STOCK_EXCHANGE,
  FAT_PROTEIN_SLUG,
  getFarmerTableCo2Headers,
  CO2_LFL_SLUG,
  getOnlyFarmerRelatedTableHeaders
} from '~/Constants';
import { StyledTab } from '~/styles/table';
import { useAuth } from '~/routes/components/AuthProvider';
import { DownloadService } from '~/api/services/downloadFiles';
import DeleteFarmer from './Dialogs/DeleteFarmer';
import ImportFarmers from './Dialogs/ImportFarmers';
import BlockFarmer from './Dialogs/BlockFarmer';
import Actions from './Actions';
import FarmerProviderComp from './FarmerProviderComp';
import Filters from './Filters';
import FilterValues from './FilterValues';
import { Popup } from 'semantic-ui-react';
import CustomIcon from '~/components/CustomIcon';
import { Link } from 'react-router-dom';

const tradingSlotSlugs = [MILK_FIXED_SLUG, MILK_STOCK_EXCHANGE, FAT_PROTEIN_SLUG]

const getActions = () => [
  {
    popupMessage: i18n.t('Farmer details'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW}#profile-details`
  },
  {
    popupMessage: i18n.t('Farmer Users'),
    type: 'link',
    name: 'users',
    size: 'large',
    link: `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW}#users`
  },
  {
    popupMessage: i18n.t(''),
    type: 'link',
    name: 'sticky note',
    renderActions: () => <></>,
    size: 'large',
    link: `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW}#communication`
  },
  {
    popupMessage: i18n.t('Article bookings'),
    type: 'link',
    name: 'shopping cart',
    size: 'large',
    link: `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW}#article-bookings`
  },
  {
    popupMessage: i18n.t('MGA attachments'),
    type: 'link',
    name: 'attach',
    size: 'large',
    link: RouteTypes.DAIRY_FACTORY_FARMERS_MGA_ATTACHMENTS
  },
  {
    popupMessage: i18n.t('Unblock'),
    type: 'icon',
    name: 'checkmark',
    size: 'large',
    color: 'green',
    actionType: 'blockUnblock',
    display: [0]
  },
  {
    popupMessage: i18n.t('Block'),
    type: 'icon',
    name: 'close',
    size: 'large',
    color: 'red',
    actionType: 'blockUnblock',
    display: [1]
  }
]

const getActionsForLightUser = () => [
  {
    popupMessage: i18n.t('Farmer details'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW}#profile-details`
  },
  {
    popupMessage: i18n.t('MGA attachments'),
    type: 'link',
    name: 'attach',
    size: 'large',
    link: RouteTypes.DAIRY_FACTORY_FARMERS_MGA_ATTACHMENTS
  }
]

const ACTIVE_FARMERS_KEY = 'active_farmers'

const defaultFilterValues = {
  page: 0,
  rows: 30,
  search: '',
  type: ACTIVE_FARMERS_KEY,
  farmerGroupId: '',
  column: '',
  sort: '',
  terminationDate: '',
  lastDeliveryDate: '',
  hasCancelledAmount: '',
  confirmedAmountFrom: '',
  confirmedAmountTo: '',
  yearlyAmountFrom: '',
  yearlyAmountTo: '',
  districtId: '',
  agricultureDistrictId: '',
  stateId: '',
  milkTypeId: '',
  periodFrom: '',
  periodTo: ''
}
const Farmers: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [dataFiltered, setDataFiltered] = useState(defaultFilterValues);
  const [blockModalData, setBlockModalData] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [deleteFarmerData, setDeleteFarmerData] = useState(null)
  const [openImportFarmersModal, setOpenImportFarmersModal] = useState(false)
  const { systemData: { mgaBilling, logged: { roleSlug }, amountUnit, modules } } = useAuth()
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const hasAnyTradingSlot = Boolean(modules.find(module => !!tradingSlotSlugs.find(tradingSlotSlug => tradingSlotSlug === module.slug)))
  const hasCo2Module = Boolean(modules.find(module =>module.slug === CO2_LFL_SLUG))
  const defaultTableColumnHeaders = getDefaultTableColumnHeaders()
  const [tableColumnHeaders, setTableColumnHeaders] = useState(defaultTableColumnHeaders)
  const hasMilkMoneyModule = modules.find(module => module.slug === MILK_MONEY_ACCOUNT_SLUG)

  const {
    isLoading: isBusyData,
    data: farmersData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = FarmersQuery.getFarmersList({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const {
    data: farmersCount,
    isLoading: isBusyFarmersCount,
    refetch: fetchCount
  } = FarmersQuery.getFarmersQueryCount({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const {
    data: farmersListOptions,
    refetch: refetchOptions,
    isLoading: isBusyFarmersList
  } = FarmersQuery.getFarmersListOptions();

  useEffect(() => {
    const defaultTableColumnHeaders = hasAnyTradingSlot ? getFarmerTableColumnHeaders(amountUnit) : getFarmerTableCo2Headers()
    setTableColumnHeaders(defaultTableColumnHeaders)
    refetchOptions()
  }, [t])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData();
      fetchCount()
    }
  }, [dataFiltered]);

  useEffect(() => {
    if (farmersListOptions) {
      setActiveTabIndex(farmersListOptions?.types.findIndex(type => type.key === ACTIVE_FARMERS_KEY))
    }
  }, [farmersListOptions])

  function getDefaultTableColumnHeaders() {
    const defaultTableColumnHead = getFarmerTableColumnHeaders(amountUnit)
    if(hasAnyTradingSlot && hasCo2Module) {
      return defaultTableColumnHead
    }
    
    if (hasAnyTradingSlot && !hasCo2Module) {
      return defaultTableColumnHead.filter(column => column.key !== 'soldMilkEconomicAllocationFpcmIdf')
    }
    
    if(!hasAnyTradingSlot && hasCo2Module) {
      return getFarmerTableCo2Headers()
    }

    if(!hasAnyTradingSlot && !hasCo2Module) {
      return getOnlyFarmerRelatedTableHeaders()
    }
  }

  function renderFilters() {
    return <Filters
      dataFiltered={dataFiltered}
      handleChangeData={(data) => {
        if (JSON.stringify(data) !== JSON.stringify(dataFiltered)) {
          setDataFiltered(data)
        }
      }}
      farmersListOptions={farmersListOptions}
    />
  }

  async function handleDownloadPdf(id) {
    setLoadingAction({
      type: 'pdf',
      id: id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFile(`farmers/${id}/generate-milk-payment-pdf`, 'term_pdf')
    } catch (error) {
      if (error.response.data) {
        // notify(error.response.data.message, 'error');
      } else {
        // notify(t('General error'), 'error');
      }
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'blockUnblock':
        return setBlockModalData(row)
      case 'delete':
        return setDeleteFarmerData(row)
      case 'pdf':
        return handleDownloadPdf(row?.id)
      default:
        return
    }
  }

  function getActionsBasedOnRole() {
    switch (roleSlug) {
      case LIGHT_USER_ROLE_SLUG:
        return getActionsForLightUser()
      case FARMER_MANAGER_ROLE_SLUG:
        return [getActionsForLightUser()[0]]
      default:
        return getActions()
    }
  }

  const renderNoteAction = (row) => {
    return row?.latestNote && <Link to={`${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW.replace(':id', row.id)}#communication`}>
      <Popup
        content={row?.latestNote?.note}
        trigger={<CustomIcon
          style={{ cursor: 'pointer' }}
          name='sticky note'
          size='large'
        />}
      />
    </Link>
  }

  function renderContent() {
    let actionsFiltered = getActionsBasedOnRole()
    if (!hasMilkMoneyModule) {
      //@ts-ignore
      actionsFiltered = actionsFiltered?.filter(action => action.name !== 'shopping cart')
    }
    const noteActionIndex = actionsFiltered.findIndex(action => action.name === 'sticky note')
    if(noteActionIndex >= 0) {
      //@ts-ignore
      actionsFiltered[noteActionIndex].renderActions = renderNoteAction
    }
    
    return <TableComponent
    //@ts-ignore
      actions={mgaBilling ? actionsFiltered : actionsFiltered?.filter(action => action.name !== 'attach')}
      isFetchingData={isFetchingData}
      loadingAction={loadingAction}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      rows={rows}
      total={filtered}
    />
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      const status = farmersListOptions.types.find((_, indexOption) => indexOption === activeIndex);
      setActiveTabIndex(activeIndex)
      setDataFiltered({
        ...dataFiltered,
        type: status.key,
        page: 0
      });
    }
  }

  function renderPanes() {
    const panes = farmersListOptions.types.map((option) => {
      const countKey = Object.keys(farmersCount).find((count) => count === option.key);
      return {
        menuItem: `${option.label}  (${farmersCount[countKey] || 0})`,
        render: () => renderContent()
      };
    });

    return (
      <StyledTab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        activeIndex={activeTabIndex}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    );
  }

  function renderActions() {
    return <Actions
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      dataFiltered={dataFiltered}
      defaultFilterValues={defaultFilterValues}
      lightUserLogged={lightUserLogged}
      handleImportClick={() => setOpenImportFarmersModal(true)}
      farmersListOptions={farmersListOptions}
    />
  }

  function renderDialogs() {
    return <>
      <DeleteFarmer
        deleteFarmerData={deleteFarmerData}
        handleClose={(submited) => {
          setDeleteFarmerData(null)
          if (submited) {
            fetchFilteredData();
            fetchCount()
          }
        }}
      />
      <ImportFarmers
        openImportFarmersModal={openImportFarmersModal}
        handleClose={() => setOpenImportFarmersModal(null)}
        farmersListOptions={farmersListOptions?.farmerGroups?.map(farmerGroup => ({ value: farmerGroup.id, text: farmerGroup.name })) || []}
      />
      <BlockFarmer
        blockModalData={blockModalData}
        handleClose={(submited) => {
          setBlockModalData(null)
          if (submited) {
            fetchFilteredData()
          }
        }}
      />
    </>
  }

  const { filtered, rows } = farmersData ? farmersData : { filtered: 0, rows: [] };
  const loading = isBusyData || isBusyFarmersList || isBusyFarmersCount;

  return (
    <FarmerProviderComp>
      <Layout
        isBusy={loading}
        segmented
        filters={farmersListOptions && !loading && renderFilters()}
        actions={renderActions()}
        title={t('Farmers')}
        actionAlignToTop
        actionNoWrap
      >
        {renderDialogs()}
        <FilterValues
          dataFiltered={dataFiltered}
          handleChangeDataFiltered={(data) => setDataFiltered(data)}
          farmersListOptions={farmersListOptions}
        />
        {farmersData && farmersListOptions && farmersCount && renderPanes()}
      </Layout>
    </FarmerProviderComp>
  );
};

export default Farmers;
